import {
  getSubSectionName,
  isNavSubSectionPathActive,
  isSectionGroup,
  type Section,
  type SubSection,
} from '@containers/Layout/NavMenu/index.util';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import { BetaBadge } from '@ovComponents/3-pattern/betaBadge/betaBadge';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalStats } from '@providers/globalStatsHooks';
import {
  Box,
  Icon,
  NavItem,
  Stack,
  type SxProps,
  useTheme,
} from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';

export type NavMenuItemProp = {
  section: Section;
  pathname: string;
  onNavigate: () => void;
  sx?: SxProps;
};

const CountBadge = ({ count }: { count: number }) => {
  const { color, borderRadius, font } = useTheme();
  return (
    <Box
      sx={{
        minWidth: 16,
        px: 0.5,
        py: 0.125,
        borderRadius: borderRadius.round,
        backgroundColor: color.negative,
        color: color.onNegative,
        fontFamily: font.base,
        fontSize: '11px',
        fontWeight: 600,
        lineHeight: '14px',
        letterSpacing: '0.25px',
        textAlign: 'center',
      }}
    >
      {count > 999 ? '999+' : String(count)}
    </Box>
  );
};

const sectionIcon = (icon: Section['icon']) => {
  if (typeof icon === 'string') {
    return <Icon>{icon}</Icon>;
  }
  const SvgIcon = icon;
  return <SvgIcon />;
};

const nameText = (
  translateBackendString: (translatedObject: unknown) => string,
  name: SubSection['name'],
  t: (key: string) => string,
) => (typeof name === 'string' ? t(name) : translateBackendString(name));

const itemMeta = ({ beta, count }: { beta?: boolean; count?: number }) => {
  const hasCount = count != null && count > 0;
  if (!beta && !hasCount) {
    return undefined;
  }

  return (
    <Stack
      direction='row'
      useFlexGap
      spacing={1}
      sx={{ alignItems: 'center', flexShrink: 0, marginRight: 1.5 }}
    >
      {hasCount && <CountBadge count={count} />}
      {beta && <BetaBadge variant='small' />}
    </Stack>
  );
};

export const NavMenuItem = ({
  section,
  pathname,
  onNavigate,
  sx,
}: NavMenuItemProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['navMenu']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const globalStats = useGlobalStats();

  const onItemClick = (analyticEvent?: string) => {
    if (analyticEvent) sendAnalytic(analyticEvent);
    onNavigate();
  };

  if (!isSectionGroup(section)) {
    const title = getSubSectionName(translateBackendString, section.name);
    return (
      <NavItem
        icon={sectionIcon(section.icon)}
        label={nameText(translateBackendString, section.name, t)}
        selected={isNavSubSectionPathActive(section, pathname)}
        badge={itemMeta({
          beta: section.beta,
          count: section.getCount?.({ stats: globalStats }),
        })}
        component={Link}
        to={section.path}
        data-testid={kebabCase(title)}
        onClick={() => onItemClick(section.analyticEvent)}
        sx={sx}
      />
    );
  }

  const childSelected = section.subSections.some((sub) =>
    isNavSubSectionPathActive(sub, pathname),
  );

  return (
    <NavItem
      icon={sectionIcon(section.icon)}
      label={nameText(translateBackendString, section.name, t)}
      defaultOpen={childSelected}
      selected={childSelected}
      badge={itemMeta({
        beta: section.beta,
      })}
      data-testid={`${kebabCase(getSubSectionName(translateBackendString, section.name))}-nav-section`}
      sx={sx}
    >
      {section.subSections.map((sub) => {
        const title = getSubSectionName(translateBackendString, sub.name);
        return (
          <NavItem
            key={title}
            variant='secondary'
            label={nameText(translateBackendString, sub.name, t)}
            selected={isNavSubSectionPathActive(sub, pathname)}
            badge={itemMeta({
              beta: sub.beta,
              count: sub.getCount?.({ stats: globalStats }),
            })}
            component={Link}
            to={sub.path}
            data-testid={kebabCase(title)}
            onClick={() => onItemClick(sub.analyticEvent)}
          />
        );
      })}
    </NavItem>
  );
};
